'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

const FAQSection = () => {
  const { t, language } = useLanguage();
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const faqs = [
    {
      id: 1,
      question: 'Is it ethical to charge for spiritual services?',
      questionEs: '¿Es ético cobrar por servicios espirituales?',
      answer: 'Absolutely. As a certified ambassador, you offer a professional service that requires training, dedication, and personal energy. Charging for your expertise allows you to fully dedicate yourself to helping others while creating a balanced energy exchange.',
      answerEs: 'Absolutamente. Como embajador certificado, ofreces un servicio profesional que requiere entrenamiento, dedicación y energía personal. Cobrar por tu experiencia te permite dedicarte completamente a ayudar a otros mientras creas un intercambio energético equilibrado.',
    },
    {
      id: 2,
      question: 'Do I need previous experience?',
      questionEs: '¿Necesito experiencia previa?',
      answer: 'No formal experience is required. Our program is designed for people with genuine interest in transformation, regardless of professional background. Full training and certification are provided.',
      answerEs: 'No se requiere experiencia formal. Nuestro programa está diseñado para personas con interés genuino en la transformación, independientemente de su formación profesional. Se proporciona entrenamiento y certificación completos.',
    },
    {
      id: 3,
      question: 'How much can I realistically earn?',
      questionEs: '¿Cuánto puedo ganar de manera realista?',
      answer: 'Earnings depend on your level and dedication. GAIA, CRYSTAL, and DIAMOND packages offer up to 20% commission, while ELITE offers up to $500 USD per sale. Many ambassadors earn $3,000–$8,000 monthly within their first year.',
      answerEs: 'Las ganancias dependen de tu nivel y dedicación. Los paquetes GAIA, CRYSTAL y DIAMOND ofrecen hasta 20% de comisión, mientras que ELITE ofrece hasta $500 USD por venta. Muchos embajadores ganan $3,000–$8,000 mensuales en su primer año.',
    },
    {
      id: 4,
      question: 'Can I work from any country?',
      questionEs: '¿Puedo trabajar desde cualquier país?',
      answer: 'Yes. Our program is fully remote and our ambassadors operate in over 30 countries. Sessions can be conducted in-person or virtually, giving you complete flexibility.',
      answerEs: 'Sí. Nuestro programa es completamente remoto y nuestros embajadores operan en más de 30 países. Las sesiones pueden realizarse en persona o virtualmente, dándote total flexibilidad.',
    },
    {
      id: 5,
      question: 'What support do I receive?',
      questionEs: '¿Qué apoyo recibo?',
      answer: 'You receive comprehensive onboarding, ongoing training, marketing resources, a personal profile page, access to the global ambassador community, and dedicated support from our team.',
      answerEs: 'Recibes incorporación completa, entrenamiento continuo, recursos de marketing, una página de perfil personal, acceso a la comunidad global de embajadores y soporte dedicado de nuestro equipo.',
    },
    {
      id: 6,
      question: 'Is there a satisfaction guarantee?',
      questionEs: '¿Hay garantía de satisfacción?',
      answer: 'Yes. We offer a 60-day satisfaction guarantee. If after completing the first modules you feel the program isn\'t right for you, we\'ll provide a full refund.',
      answerEs: 'Sí. Ofrecemos una garantía de satisfacción de 60 días. Si después de completar los primeros módulos sientes que el programa no es para ti, te proporcionaremos un reembolso completo.',
    },
  ];

  return (
    <section className="py-32 bg-white">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-[#1D6FE8]" />
            <span className="text-sm font-body font-medium text-[#1D6FE8] tracking-wide uppercase">FAQ</span>
          </div>
          <h2 className="font-display font-bold text-[#0A1628] leading-tight tracking-tight mb-4"
            style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)' }}>
            {t('faq.headline')}
          </h2>
          <p className="font-body text-lg text-[#4A5568]">
            {t('faq.subtitle')}
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-3">
          {faqs?.map((faq) => (
            <div
              key={faq?.id}
              className={`rounded-2xl border transition-all duration-350 ${
                expandedFAQ === faq?.id
                  ? 'border-[#1D6FE8]/30 bg-[#EEF4FD]'
                  : 'border-[#E8EDF5] bg-white hover:border-[#1D6FE8]/20'
              }`}
            >
              <button
                onClick={() => setExpandedFAQ(expandedFAQ === faq?.id ? null : faq?.id)}
                className="w-full px-6 py-5 text-left flex items-center justify-between gap-4"
              >
                <span className="font-display font-semibold text-[#0A1628] text-base leading-snug">
                  {language === 'es' ? faq?.questionEs : faq?.question}
                </span>
                <div className={`w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-350 ${
                  expandedFAQ === faq?.id ? 'bg-[#1D6FE8] rotate-180' : 'bg-[#F4F7FC]'
                }`}>
                  <Icon
                    name="ChevronDownIcon"
                    size={16}
                    className={expandedFAQ === faq?.id ? 'text-white' : 'text-[#4A5568]'}
                  />
                </div>
              </button>

              {expandedFAQ === faq?.id && (
                <div className="px-6 pb-5 animate-fade-up">
                  <p className="font-body text-[#4A5568] leading-relaxed text-base">
                    {language === 'es' ? faq?.answerEs : faq?.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact */}
        <div className="mt-12 text-center">
          <p className="font-body text-[#4A5568] mb-4">Still have questions?</p>
          <a
            href="https://wa.link/anasilvialara"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#F4F7FC] text-[#0A1628] font-display font-semibold text-sm px-6 py-3 rounded-xl hover:bg-[#EEF4FD] border border-[#E8EDF5] hover:border-[#1D6FE8]/30 transition-all duration-250"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={16} />
            Chat with us
          </a>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;